import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck } from "lucide-react";
import { Field } from "../site/ContactForm";
import { usePickupDays } from "../../lib/pickup";
import { restaurant } from "../../data/siteData";

const EMPTY = { date: "", slot: "", name: "", phone: "", notes: "", cgv: false };

const PickupFields = ({ days, status, form, set }) => {
  if (status === "loading") return <p className="text-sm text-[#A2B3A7]" data-testid="checkout-slots-loading">Chargement des créneaux…</p>;
  if (!days.length) return <p className="text-sm text-[#F0A385]" data-testid="checkout-no-slots">Aucun créneau disponible pour le moment. Appelez-nous au {restaurant.phone}.</p>;
  const day = days.find((d) => d.date === form.date) ?? days[0];
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Field label="Jour de retrait *" id="co-day">
        <select id="co-day" value={day.date} onChange={set("date")} className="field" data-testid="checkout-day-select">
          {days.map((d) => <option key={d.date} value={d.date}>{d.label}</option>)}
        </select>
      </Field>
      <Field label="Heure de retrait *" id="co-slot">
        <select id="co-slot" value={form.slot || day.slots[0].value} onChange={set("slot")} className="field" data-testid="checkout-slot-select">
          {day.slots.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </Field>
    </div>
  );
};

export const OrderForm = ({ initial, onReview }) => {
  const { days, status } = usePickupDays();
  const [form, setForm] = useState(initial ?? EMPTY);
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v, ...(k === "date" ? { slot: "" } : {}) }));
  };

  const submit = (e) => {
    e.preventDefault();
    const day = days.find((d) => d.date === form.date) ?? days[0];
    const slot = day?.slots.find((s) => s.value === form.slot) ?? day?.slots[0];
    if (!day || !slot) return toast.error("Aucun créneau de retrait disponible pour le moment.");
    const name = form.name.trim();
    const phone = form.phone.trim();
    if (!name) return toast.error("Merci d’indiquer votre nom.");
    const digits = phone.replace(/\D/g, "").length;
    if (digits < 9 || digits > 15) return toast.error("Merci d’indiquer un numéro de téléphone valide.");
    if (!form.cgv) return toast.error("Merci d’accepter les conditions générales de vente.");
    onReview({ form: { ...form, date: day.date, slot: slot.value }, details: { name, phone, notes: form.notes.trim(), day, slot } });
  };

  return (
    <form onSubmit={submit} noValidate className="mt-6 space-y-5" data-testid="checkout-form">
      <PickupFields days={days} status={status} form={form} set={set} />
      <Field label="Nom *" id="co-name"><input id="co-name" required maxLength={100} autoComplete="name" value={form.name} onChange={set("name")} className="field" data-testid="checkout-name" /></Field>
      <Field label="Téléphone *" id="co-phone"><input id="co-phone" type="tel" required maxLength={30} autoComplete="tel" value={form.phone} onChange={set("phone")} className="field" data-testid="checkout-phone" /></Field>
      <Field label="Remarques (facultatif)" id="co-notes"><textarea id="co-notes" rows={2} maxLength={500} value={form.notes} onChange={set("notes")} className="field resize-y" placeholder="Type de pâtes (spaghetti, tagliatelle ou penne), allergies…" data-testid="checkout-notes" /></Field>
      <label className="flex items-start gap-3 text-sm text-[#D6CFC0] cursor-pointer">
        <input type="checkbox" checked={form.cgv} onChange={set("cgv")} className="mt-1 w-4 h-4 accent-[#D4A359]" data-testid="checkout-cgv" />
        <span>
          J’accepte les <Link to="/conditions-generales-de-vente" className="underline text-[#E5B66C]">conditions générales de vente</Link> et la{" "}
          <Link to="/politique-de-confidentialite" className="underline text-[#E5B66C]">politique de confidentialité</Link>.
        </span>
      </label>
      <button type="submit" disabled={!days.length} className="btn-gold w-full disabled:opacity-60" data-testid="checkout-review-button">
        <ClipboardCheck className="w-4 h-4" /> Vérifier ma commande
      </button>
      <p className="text-xs text-[#A2B3A7] text-center" data-testid="checkout-pay-on-site">Aucun paiement en ligne : vous réglez votre commande au restaurant, lors du retrait.</p>
    </form>
  );
};
